import { invokeWithRetry } from "./common/invokeWithRetry";
import { SubscriptionType, SubscriptionTypeFormData, SubscriptionTypeImportData } from "@/types/subscriptionType";

const FUNCTION_NAME = 'subscription-types-management';

export async function getSubscriptionTypes(
  gymId: string,
  includeInactive: boolean = false
): Promise<SubscriptionType[]> {
  const { data, error, authExpired } = await invokeWithRetry(
    FUNCTION_NAME,
    {
      action: 'get_types',
      gym_id: gymId,
      include_inactive: includeInactive
    }
  );

  if (authExpired) return [];
  if (error) throw new Error(error.message || 'Failed to fetch subscription types');

  return data?.types || [];
}

export async function getSubscriptionTypeById(
  gymId: string,
  typeId: string
): Promise<SubscriptionType | null> {
  const { data, error, authExpired } = await invokeWithRetry(
    FUNCTION_NAME,
    {
      action: 'get_type_by_id',
      gym_id: gymId,
      type_id: typeId
    }
  );

  if (authExpired) return null;
  if (error) throw new Error(error.message || 'Failed to fetch subscription type');

  return data?.type || null;
}

export async function createSubscriptionType(
  gymId: string,
  typeData: SubscriptionTypeFormData
): Promise<SubscriptionType> {
  const { data, error, authExpired } = await invokeWithRetry(
    FUNCTION_NAME,
    {
      action: 'create_type',
      gym_id: gymId,
      data: typeData
    }
  );

  if (authExpired) throw new Error('Session expired');
  if (error) throw new Error(error.message || 'Failed to create subscription type');

  return data?.type;
}

export async function updateSubscriptionType(
  gymId: string,
  typeId: string,
  typeData: Partial<SubscriptionTypeFormData>
): Promise<SubscriptionType> {
  const { data, error, authExpired } = await invokeWithRetry(
    FUNCTION_NAME,
    {
      action: 'update_type',
      gym_id: gymId,
      type_id: typeId,
      data: typeData
    }
  );

  if (authExpired) throw new Error('Session expired');
  if (error) throw new Error(error.message || 'Failed to update subscription type');

  return data?.type;
}

export async function deleteSubscriptionType(
  gymId: string,
  typeId: string
): Promise<boolean> {
  const { data, error, authExpired } = await invokeWithRetry(
    FUNCTION_NAME,
    {
      action: 'delete_type',
      gym_id: gymId,
      type_id: typeId
    }
  );

  if (authExpired) throw new Error('Session expired');
  if (error) throw new Error(error.message || 'Failed to delete subscription type');

  return data?.success || false;
}

export async function importSubscriptionTypes(
  gymId: string,
  types: SubscriptionTypeImportData[]
): Promise<{ created: number; updated: number; errors: string[] }> {
  const { data, error, authExpired } = await invokeWithRetry(
    FUNCTION_NAME,
    {
      action: 'import_types',
      gym_id: gymId,
      types
    }
  );

  if (authExpired) throw new Error('Session expired');
  if (error) throw new Error(error.message || 'Failed to import subscription types');

  return data?.results || { created: 0, updated: 0, errors: [] };
}
